import type {
  CauseAnalysisMethod,
  HypothesisInvestigation,
  HypothesisInvestigationMethod,
  HypothesisWhy,
  IshikawaCause,
  IshikawaHypothesis,
  IshikawaHypothesisStatus,
  RootCauseAnalysis,
  RootCauseCategory,
} from '../types/issue';

export const SIX_M: RootCauseCategory[] = ['method', 'machine', 'material', 'manpower', 'measurement', 'environment'];

const HYPOTHESIS_STATUSES: IshikawaHypothesisStatus[] = ['candidate', 'investigating', 'discarded', 'confirmed'];

const METHOD_MAP: Record<string, CauseAnalysisMethod> = {
  five_whys: 'five_whys',
  ishikawa: 'ishikawa',
  simple: 'free',
};

function isHypothesis(value: unknown): value is IshikawaHypothesis {
  return Boolean(value && typeof value === 'object' && 'text' in (value as IshikawaHypothesis));
}

/** Texto de uma hipótese, seja string antiga ou objeto. */
export function hypothesisText(cause: string | IshikawaHypothesis | null | undefined): string {
  if (!cause) return '';
  if (typeof cause === 'string') return cause.trim();
  return (cause.text ?? '').trim();
}

function hypothesisStatus(value: unknown): IshikawaHypothesisStatus {
  return HYPOTHESIS_STATUSES.includes(value as IshikawaHypothesisStatus)
    ? value as IshikawaHypothesisStatus
    : 'candidate';
}

/** Converte string legada ou objeto incompleto na hipótese estruturada. */
export function normalizeHypothesis(
  cause: string | IshikawaHypothesis,
  category: RootCauseCategory,
  index: number,
): IshikawaHypothesis | null {
  if (typeof cause === 'string' || !isHypothesis(cause)) {
    const text = hypothesisText(typeof cause === 'string' ? cause : null);
    if (!text) return null;
    return {
      id: `legacy:${category}:${index}:${text}`,
      text,
      category,
      status: 'candidate',
      probable: false,
    };
  }
  const text = hypothesisText(cause);
  if (!text) return null;
  return {
    id: cause.id?.trim() || `legacy:${category}:${index}:${text}`,
    text,
    category,
    status: hypothesisStatus(cause.status),
    probable: cause.probable === true,
    investigation: normalizeInvestigation(cause.investigation),
  };
}

export function isLegacyHypothesisId(id: string): boolean {
  return id.startsWith('legacy:');
}

function normalizeInvestigation(value: HypothesisInvestigation | undefined): HypothesisInvestigation | undefined {
  if (!value) return undefined;
  const method: HypothesisInvestigationMethod | undefined = value.method === 'five_whys' || value.method === 'direct'
    ? value.method
    : undefined;
  const whys = (value.whys ?? [])
    .map((why) => ({ id: why.id?.trim() || '', answer: why.answer?.trim() || '' }))
    .filter((why) => why.id && why.answer);
  const conclusion = value.conclusion?.trim() || undefined;
  const evidence = value.evidence?.trim() || undefined;
  const discardReason = value.discard_reason?.trim() || undefined;
  if (!method && whys.length === 0 && !conclusion && !evidence && !discardReason) return undefined;
  return {
    method: method ?? 'direct',
    whys: whys.length > 0 ? whys : undefined,
    whys_closed: value.whys_closed === true,
    conclusion,
    evidence,
    discard_reason: discardReason,
  };
}

/** Troca ids legacy: por ids definitivos no momento de persistir. Mantém texto, categoria, estado e investigação. */
export function durableIshikawa(groups: IshikawaCause[] | undefined): IshikawaCause[] {
  return ensureIshikawa(groups).map((group) => ({
    ...group,
    causes: group.causes.map((cause) => (
      isHypothesis(cause) && isLegacyHypothesisId(cause.id)
        ? { ...cause, id: newHypothesisId() }
        : cause
    )),
  }));
}

export function assignDurableHypothesisId(
  groups: IshikawaCause[] | undefined,
  category: RootCauseCategory,
  id: string,
): { groups: IshikawaCause[]; id: string } {
  if (!isLegacyHypothesisId(id)) return { groups: ensureIshikawa(groups), id };
  const nextId = newHypothesisId();
  return {
    id: nextId,
    groups: ensureIshikawa(groups).map((group) => (
      group.category === category
        ? {
          ...group,
          causes: group.causes.map((cause) => (
            isHypothesis(cause) && cause.id === id ? { ...cause, id: nextId } : cause
          )),
        }
        : group
    )),
  };
}

/** Garante os seis ramos e normaliza hipóteses antigas sem apagar as já gravadas. */
export function ensureIshikawa(groups: IshikawaCause[] | undefined): IshikawaCause[] {
  return SIX_M.map((category) => ({
    category,
    causes: (groups?.find((group) => group.category === category)?.causes ?? [])
      .map((cause, index) => normalizeHypothesis(cause, category, index))
      .filter((cause): cause is IshikawaHypothesis => Boolean(cause)),
  }));
}

function newHypothesisId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return `h-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

/** Inclui hipótese como candidata. Não confirma a causa do problema. */
export function addIshikawaHypothesis(
  groups: IshikawaCause[] | undefined,
  category: RootCauseCategory,
  text: string,
): IshikawaCause[] {
  const hypothesis = text.trim();
  if (!hypothesis) return ensureIshikawa(groups);
  const item: IshikawaHypothesis = {
    id: newHypothesisId(),
    text: hypothesis,
    category,
    status: 'candidate',
    probable: false,
  };
  return ensureIshikawa(groups).map((group) => (
    group.category === category
      ? { ...group, causes: [...group.causes, item] }
      : group
  ));
}

/** Edita o texto e preserva id, estado e a marca de provável. */
export function updateIshikawaHypothesis(
  groups: IshikawaCause[] | undefined,
  category: RootCauseCategory,
  id: string,
  text: string,
): IshikawaCause[] {
  const hypothesis = text.trim();
  if (!hypothesis) return ensureIshikawa(groups);
  return ensureIshikawa(groups).map((group) => (
    group.category === category
      ? {
        ...group,
        causes: group.causes.map((cause) => (
          isHypothesis(cause) && cause.id === id ? { ...cause, text: hypothesis } : cause
        )),
      }
      : group
  ));
}

export function removeIshikawaHypothesis(
  groups: IshikawaCause[] | undefined,
  category: RootCauseCategory,
  id: string,
): IshikawaCause[] {
  return ensureIshikawa(groups).map((group) => (
    group.category === category
      ? { ...group, causes: group.causes.filter((cause) => !(isHypothesis(cause) && cause.id === id)) }
      : group
  ));
}

/**
 * Marca ou desmarca “mais provável”.
 * Candidata marcada passa a investigating. Desmarcar investigating volta a candidate.
 * Não altera cause_confirmed.
 */
export function toggleIshikawaProbable(
  groups: IshikawaCause[] | undefined,
  category: RootCauseCategory,
  id: string,
): IshikawaCause[] {
  return ensureIshikawa(groups).map((group) => (
    group.category === category
      ? {
        ...group,
        causes: group.causes.map((cause) => {
          if (!isHypothesis(cause) || cause.id !== id) return cause;
          const probable = !cause.probable;
          let status = cause.status;
          if (probable && status === 'candidate') status = 'investigating';
          if (!probable && status === 'investigating') status = 'candidate';
          return { ...cause, probable, status };
        }),
      }
      : group
  ));
}

function mapHypothesis(
  groups: IshikawaCause[] | undefined,
  category: RootCauseCategory,
  id: string,
  update: (item: IshikawaHypothesis) => IshikawaHypothesis,
): IshikawaCause[] {
  return ensureIshikawa(groups).map((group) => (
    group.category === category
      ? {
        ...group,
        causes: group.causes.map((cause) => (
          isHypothesis(cause) && cause.id === id ? update(cause) : cause
        )),
      }
      : group
  ));
}

function findHypothesis(
  groups: IshikawaCause[] | undefined,
  category: RootCauseCategory,
  id: string,
): IshikawaHypothesis | null {
  const cause = ensureIshikawa(groups)
    .find((group) => group.category === category)
    ?.causes.find((item) => isHypothesis(item) && item.id === id);
  return cause && isHypothesis(cause) ? cause : null;
}

function withInvestigation(
  item: IshikawaHypothesis,
  method: HypothesisInvestigationMethod,
  patch: Partial<HypothesisInvestigation>,
): IshikawaHypothesis {
  const current = item.investigation ?? { method };
  return {
    ...item,
    status: item.status === 'candidate' ? 'investigating' : item.status,
    investigation: { ...current, method, ...patch },
  };
}

/** Escolhe o método da hipótese. Não confirma a causa do problema. */
export function setHypothesisMethod(
  groups: IshikawaCause[] | undefined,
  category: RootCauseCategory,
  id: string,
  method: HypothesisInvestigationMethod,
): IshikawaCause[] {
  return mapHypothesis(groups, category, id, (item) => (
    item.status === 'confirmed' || item.status === 'discarded'
      ? item
      : withInvestigation(item, method, {})
  ));
}

/** Acrescenta um porquê. A quantidade não é fixa e o último não confirma a hipótese. */
export function addHypothesisWhy(
  groups: IshikawaCause[] | undefined,
  category: RootCauseCategory,
  id: string,
  answer: string,
): IshikawaCause[] {
  const text = answer.trim();
  if (!text) return ensureIshikawa(groups);
  return mapHypothesis(groups, category, id, (item) => {
    if (item.status === 'confirmed' || item.status === 'discarded' || item.investigation?.whys_closed) return item;
    const why: HypothesisWhy = { id: newHypothesisId(), answer: text };
    const whys = [...(item.investigation?.whys ?? []), why];
    return withInvestigation(item, 'five_whys', { whys });
  });
}

export function closeHypothesisWhys(
  groups: IshikawaCause[] | undefined,
  category: RootCauseCategory,
  id: string,
): IshikawaCause[] {
  return mapHypothesis(groups, category, id, (item) => (
    item.investigation?.method === 'five_whys'
      ? withInvestigation(item, 'five_whys', { whys_closed: true })
      : item
  ));
}

export function setHypothesisFinding(
  groups: IshikawaCause[] | undefined,
  category: RootCauseCategory,
  id: string,
  finding: { conclusion?: string; evidence?: string; discard_reason?: string },
): IshikawaCause[] {
  return mapHypothesis(groups, category, id, (item) => {
    if (item.status === 'confirmed' || item.status === 'discarded') return item;
    const method = item.investigation?.method ?? 'direct';
    return withInvestigation(item, method, finding);
  });
}

/** Confirma a hipótese. Exige conclusão e evidência. Não escreve cause_confirmed. */
export function confirmHypothesisCause(
  groups: IshikawaCause[] | undefined,
  category: RootCauseCategory,
  id: string,
): IshikawaCause[] | null {
  const item = findHypothesis(groups, category, id);
  const conclusion = item?.investigation?.conclusion?.trim() ?? '';
  const evidence = item?.investigation?.evidence?.trim() ?? '';
  if (!item || !conclusion || !evidence) return null;
  return mapHypothesis(groups, category, id, (hypothesis) => ({
    ...hypothesis,
    status: 'confirmed',
    investigation: {
      method: hypothesis.investigation?.method ?? 'direct',
      ...hypothesis.investigation,
      conclusion,
      evidence,
    },
  }));
}

/** Descarta a hipótese e a mantém no ramo. Exige justificativa. */
export function discardHypothesis(
  groups: IshikawaCause[] | undefined,
  category: RootCauseCategory,
  id: string,
): IshikawaCause[] | null {
  const item = findHypothesis(groups, category, id);
  const reason = item?.investigation?.discard_reason?.trim() ?? '';
  if (!item || !reason) return null;
  return mapHypothesis(groups, category, id, (hypothesis) => ({
    ...hypothesis,
    status: 'discarded',
    investigation: {
      method: hypothesis.investigation?.method ?? 'direct',
      ...hypothesis.investigation,
      discard_reason: reason,
    },
  }));
}

export function ishikawaSummary(groups: IshikawaCause[] | undefined): {
  total: number;
  probable: number;
  investigating: number;
  discarded: number;
  confirmed: number;
  byCategory: Record<RootCauseCategory, number>;
} {
  const branches = ensureIshikawa(groups);
  const items = branches.flatMap((group) => group.causes.filter(isHypothesis));
  return {
    total: items.length,
    probable: items.filter((item) => item.probable).length,
    investigating: items.filter((item) => item.status === 'investigating').length,
    discarded: items.filter((item) => item.status === 'discarded').length,
    confirmed: items.filter((item) => item.status === 'confirmed').length,
    byCategory: SIX_M.reduce((counts, category) => {
      counts[category] = branches.find((group) => group.category === category)?.causes.length ?? 0;
      return counts;
    }, {} as Record<RootCauseCategory, number>),
  };
}

export function reviseHypothesis(analysis: RootCauseAnalysis, text: string): RootCauseAnalysis {
  return { ...analysis, root_cause_description: text, cause_confirmed: false };
}

/** Confirmação só por este comando. O último porquê e o item 6M não passam por aqui. */
export function confirmCauseDraft(
  analysis: RootCauseAnalysis,
  method: 'five_whys' | 'ishikawa' | 'simple' | null,
): RootCauseAnalysis | null {
  if (!analysis.root_cause_description.trim() || !(analysis.cause_evidence ?? '').trim()) return null;
  return {
    ...analysis,
    root_cause_description: analysis.root_cause_description.trim(),
    cause_evidence: analysis.cause_evidence?.trim(),
    cause_confirmed: true,
    analysis_method: method ? METHOD_MAP[method] : analysis.analysis_method,
  };
}
